import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { useHomeContent } from "../context/HomeContentContext";
import { useSiteInfo } from "../context/SiteInfoContext";

export default function Home() {
  const { content } = useHomeContent();
  const { info } = useSiteInfo();
  const [featured, setFeatured] = useState([]);
  const [aiRecs, setAiRecs] = useState({ products: [], reason: "" });
  const [loadingAi, setLoadingAi] = useState(true);

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then(({ data }) => setFeatured(data.items || []));
    api.post("/ai/recommend", { interests: "elegant everyday luxury for a modern woman" })
      .then(({ data }) => setAiRecs(data))
      .catch(() => {})
      .finally(() => setLoadingAi(false));
  }, []);

  const headlineLines = (content.hero_headline || "").split("\n");
  const tiles = content.category_tiles || [];
  const marquee = content.marquee_items || [];

  return (
    <main data-testid="home-page">
      <Seo title={null}
           description={`${info.brand_name} — ${content.hero_subtagline}`}
           jsonLd={{
             "@context": "https://schema.org",
             "@type": "Organization",
             "name": info.brand_name,
             "url": info.website_url,
             "logo": info.logo_url || undefined,
             "address": { "@type": "PostalAddress", "addressLocality": "London", "addressCountry": "GB" }
           }} />
      {/* HERO */}
      <section className="relative h-[92vh] overflow-hidden bg-cream" data-testid="hero">
        <motion.div initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: "easeOut" }}
          className="absolute inset-0">
          {content.hero_image && <img src={content.hero_image} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        </motion.div>

        <div className="relative z-10 h-full flex items-end pb-20 md:pb-28">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 text-white w-full">
            {content.hero_eyebrow && (
              <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="eyebrow opacity-90" data-testid="hero-eyebrow">{content.hero_eyebrow}</motion.p>
            )}
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.45 }}
              className="font-serif text-5xl md:text-7xl lg:text-[7rem] leading-[0.95] tracking-tight max-w-4xl mt-3"
              data-testid="hero-headline">
              {headlineLines.map((l, i) => <span key={i}>{l}{i < headlineLines.length - 1 && <br />}</span>)}
            </motion.h1>
            {content.hero_subtagline && (
              <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
                className="text-base md:text-lg font-light max-w-md mt-6 opacity-90"
                data-testid="hero-subtagline">{content.hero_subtagline}</motion.p>
            )}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mt-9 flex gap-4 flex-wrap">
              {content.hero_cta_label && (
                <Link to={content.hero_cta_link || "/shop"} className="btn-primary !bg-white !text-black hover:!bg-[var(--gold)] hover:!text-white !border-white" data-testid="hero-cta-shop">
                  {content.hero_cta_label}
                </Link>
              )}
              {content.hero_secondary_label && (
                <Link to={content.hero_secondary_link || "/journal"} className="btn-ghost text-white border-white" data-testid="hero-cta-secondary">
                  {content.hero_secondary_label}
                </Link>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      {marquee.length > 0 && (
        <div className="bg-cream border-y border-hair py-4 overflow-hidden" data-testid="hero-marquee">
          <div className="marquee text-[11px] tracking-[0.4em] uppercase text-neutral-700 whitespace-nowrap">
            {Array(2).fill(0).map((_, k) => (
              <div key={k} className="flex gap-16 pr-16">
                {marquee.map((m, idx) => (
                  <span key={`${k}-${idx}`} className="flex items-center gap-16"><span>{m}</span><span>·</span></span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CATEGORIES */}
      {tiles.length > 0 && (
        <section className="max-w-[1480px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="categories">
          <div className="flex justify-between items-end mb-14">
            <div>
              <p className="eyebrow text-neutral-600">{content.category_section_eyebrow}</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-2">{content.category_section_title}</h2>
            </div>
            <Link to="/shop" className="btn-ghost hidden md:inline-flex">All Pieces <ArrowRight className="w-4 h-4" strokeWidth={1.25} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {tiles.map((c, i) => (
              <Link key={c.slug || i} to={`/category/${c.slug}`} className={`group rise rise-d${i+1}`} data-testid={`cat-${c.slug}`}>
                <div className="product-img-wrap aspect-[3/4] bg-sand">
                  {c.image && <img src={c.image} alt={c.label} className="product-img w-full h-full object-cover" loading="lazy" />}
                </div>
                <p className="font-serif text-xl md:text-2xl mt-5 group-hover:text-[var(--gold)] transition-colors">{c.label}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Discover →</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* LIFESTYLE / PARALLAX BANNER */}
      {(content.lifestyle_image || content.lifestyle_headline) && (
        <section className="relative h-[80vh] overflow-hidden" data-testid="lifestyle-banner">
          <motion.div initial={{ y: 0 }} whileInView={{ y: -40 }} viewport={{ once: false }}
            transition={{ duration: 1.5 }} className="absolute inset-0">
            {content.lifestyle_image && <img src={content.lifestyle_image} alt="" className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-black/30" />
          </motion.div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-[1480px] mx-auto px-6 md:px-12 text-white">
              {content.lifestyle_eyebrow && <p className="eyebrow opacity-90">{content.lifestyle_eyebrow}</p>}
              {content.lifestyle_headline && (
                <h2 className="font-serif text-4xl md:text-6xl max-w-2xl mt-4 leading-tight">
                  {content.lifestyle_headline}
                </h2>
              )}
              {content.lifestyle_body && (
                <p className="font-light text-base md:text-lg max-w-xl mt-6 opacity-90">
                  {content.lifestyle_body}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-[1480px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="featured-section">
        <div className="flex justify-between items-end mb-12">
          <div>
            <p className="eyebrow text-neutral-600">{content.featured_eyebrow}</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-2">{content.featured_title}</h2>
          </div>
          <Link to="/shop" className="btn-ghost">Shop All <ArrowRight className="w-4 h-4" strokeWidth={1.25} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* AI STYLIST */}
      {content.ai_enabled !== false && (
        <section className="bg-cream border-y border-hair py-24 md:py-32" data-testid="ai-section">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[var(--gold)]" strokeWidth={1.25} />
              <p className="eyebrow text-neutral-600">{content.ai_eyebrow}</p>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl">{content.ai_title}</h2>
            <p className="text-base font-light text-neutral-600 max-w-xl mt-4 mb-10" data-testid="ai-body">
              {content.ai_body
                ? content.ai_body
                : (loadingAi ? "Curating a private edit…" : aiRecs.reason)}
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {(aiRecs.products || []).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
